import React, { useEffect, useState } from "react";
import { api, type Tenancy } from "../../api";
import { useAuth } from "../../AuthContext";
import { useHistoryNav } from "../../useHistoryNav";
import { useLanguage } from "../../i18n";
import { initials } from "../../format";
import { TenantHome } from "./TenantHome";
import { TenantBrowse } from "./TenantBrowse";
import { TenantPayments } from "./TenantPayments";
import { TenantRequests } from "./TenantRequests";
import { TenantSettings } from "./TenantSettings";
import { TenantPlan } from "./TenantPlan";
import { TenantSavedSearches } from "./TenantSavedSearches";
import { TenantOnboarding } from "./TenantOnboarding";
import { ReferEarn } from "../shared/ReferEarn";
import { ProfileScreen } from "../shared/ProfileScreen";
import { NotificationBell, NotificationsScreen } from "../shared/Notifications";
import { useAutoRefresh } from "../../useAutoRefresh";
import { UpdateNotice, RatingPrompt } from "../shared/AppPrompts";
import { VerifyEmailNotice } from "../shared/VerifyEmail";
import { useAutoEnablePush } from "../shared/NotificationSettings";
import { HomeIcon, SearchIcon, CardIcon, WrenchIcon, SettingsIcon } from "../shared/Icons";
import { trackScreen } from "../../analytics";
import { usePullToRefresh } from "../../usePullToRefresh";
import { navTapFx } from "../shared/navRipple";
import { useConsumePendingNav } from "../../pendingNav";
import { useLiveNotifications } from "../../liveNotifications";

type View = "home" | "browse" | "payments" | "requests" | "settings" | "profile" | "notifications" | "plans" | "refer" | "savedSearches";

export function TenantApp() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const NAV: { key: View; label: string; icon: React.ReactNode }[] = [
    { key: "home", label: t("nav.home"), icon: <HomeIcon /> },
    { key: "browse", label: t("nav.browse"), icon: <SearchIcon /> },
    { key: "payments", label: t("nav.payments"), icon: <CardIcon /> },
    { key: "requests", label: t("nav.requests"), icon: <WrenchIcon /> },
    { key: "settings", label: t("nav.settings"), icon: <SettingsIcon /> },
  ];
  const [view, pushView] = useHistoryNav<View>("home");
  // Which sub-tab to open within the target view, e.g. a visit-request
  // notification opens Requests already on the Visits tab. Reset on every
  // navigation (not just when a caller bothers to pass one) so it can never
  // linger stale from an earlier deep link once the user navigates normally.
  const [focusTab, setFocusTab] = useState<string | null>(null);
  function navigate(v: View, tab?: string) {
    setFocusTab(tab ?? null);
    pushView(v);
  }
  useConsumePendingNav((v, _propertyId, tab) => navigate(v as View, tab));
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  // undefined = still checking, null = nothing pending, Tenancy = must
  // complete onboarding before anything else in the app is reachable.
  const [pendingOnboarding, setPendingOnboarding] = useState<Tenancy | null | undefined>(undefined);
  // Bumped on pull-to-refresh and used as the active view's key, so
  // dragging down forces a full remount of whatever screen is showing —
  // re-running its own data-fetch effects, the same as navigating to it fresh.
  const [refreshKey, setRefreshKey] = useState(0);
  useAutoEnablePush();

  async function refreshUnreadNotifications() {
    const { unreadCount } = await api.get("/api/notifications/mine");
    setUnreadNotifications(unreadCount);
  }
  useLiveNotifications(setUnreadNotifications);

  async function checkOnboarding() {
    const { tenancies } = await api.get("/api/tenancies/mine");
    const pending = (tenancies as Tenancy[]).find((t) => t.onboardingStatus === "pending");
    setPendingOnboarding(pending ?? null);
  }

  const { ref: contentRef, pull, refreshing, triggerPx } = usePullToRefresh<HTMLDivElement>(async () => {
    await Promise.all([refreshUnreadNotifications(), checkOnboarding()]);
    setRefreshKey((k) => k + 1);
  });

  useAutoRefresh(() => {
    refreshUnreadNotifications();
    checkOnboarding();
    setRefreshKey((k) => k + 1);
  });

  useEffect(() => {
    checkOnboarding();
  }, []);

  useEffect(() => {
    refreshUnreadNotifications();
    trackScreen(`tenant/${view}`);
  }, [view]);

  if (pendingOnboarding === undefined) {
    return <div className="spinner" />;
  }

  if (pendingOnboarding) {
    return (
      <>
        <div className="app-header">
          <div className="wordmark-row">
            <img src="/logo.svg" alt="" className="wordmark-logo" />
            <div className="wordmark">
              <b>ToLetBook</b>
              <span>{t("nav.tenantDashboard")}</span>
            </div>
          </div>
        </div>
        <div className="content">
          <TenantOnboarding tenancy={pendingOnboarding} onDone={checkOnboarding} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="app-header">
        <div className="wordmark-row">
          <img src="/logo.svg" alt="" className="wordmark-logo" />
          <div className="wordmark">
            <b>ToLetBook</b>
            <span>{t("nav.tenantDashboard")}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <NotificationBell unreadCount={unreadNotifications} onClick={() => navigate("notifications")} />
          <button className="avatar-sm" style={{ border: "none", padding: 0, cursor: "pointer" }} onClick={() => navigate("profile")} aria-label={t("settings.profileMenu")} title={t("settings.profileMenu")}>
            {user ? initials(user.name) : ""}
          </button>
        </div>
      </div>

      <div className="content" ref={contentRef}>
        <div className="ptr-indicator" style={{ height: refreshing ? 40 : pull }}>
          {refreshing ? (
            <div className="ptr-spinner" />
          ) : (
            <svg
              className={`ptr-arrow${pull >= triggerPx ? " ready" : ""}`}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M6 13l6 6 6-6" />
            </svg>
          )}
        </div>
        <div className="greet">
          {t("common.hiGreet")} <b>{user?.name.split(" ")[0]}</b>
        </div>
        <VerifyEmailNotice />
        <UpdateNotice />
        <RatingPrompt />
        <div className="view" key={refreshKey}>
          {view === "home" && <TenantHome onNavigate={navigate} />}
          {view === "browse" && <TenantBrowse onNavigate={navigate} />}
          {view === "payments" && <TenantPayments />}
          {view === "requests" && <TenantRequests focusTab={focusTab} />}
          {view === "settings" && <TenantSettings onNavigate={navigate} />}
          {view === "plans" && <TenantPlan onBack={() => window.history.back()} />}
          {view === "refer" && <ReferEarn onBack={() => window.history.back()} />}
          {view === "savedSearches" && <TenantSavedSearches onBack={() => window.history.back()} onNavigate={(v) => navigate(v as View)} />}
          {view === "profile" && <ProfileScreen onBack={() => window.history.back()} />}
          {view === "notifications" && (
            <NotificationsScreen
              onBack={() => window.history.back()}
              onChanged={refreshUnreadNotifications}
              onNavigate={(v, tab) => navigate(v as View, tab)}
            />
          )}
        </div>
      </div>

      <nav className="bottomnav">
        {NAV.map((n) => (
          <button
            key={n.key}
            className={`nav-btn${view === n.key ? " active" : ""}`}
            onClick={(e) => {
              navTapFx(e);
              navigate(n.key);
            }}
          >
            <span className="nav-icon-wrap">{n.icon}</span>
            <span>{n.label}</span>
          </button>
        ))}
      </nav>
    </>
  );
}
